/**
 * usePinchZoom — touch pinch + wheel zoom for any container (Iter 67ed).
 *
 * Returns `{scale, panX, panY, handlers, reset}` you spread onto the
 * outer container. Coordinates are reported in container-relative
 * "before transform" space so children can still consume normalized
 * 0–100 events without knowing about the transform.
 *
 * Why not a library: we already have lucide + dompurify + radix; adding
 * react-zoom-pan-pinch (32 KB) for a single component felt heavy. The
 * implementation below is ~90 lines, handles two-finger pinch + wheel
 * + double-tap-to-reset, and clamps scale to a sensible range. Pan
 * happens via single-finger drag when zoomed in.
 */
import { useCallback, useRef, useState } from "react";

const MIN_SCALE = 1;
const MAX_SCALE = 4;
const DOUBLE_TAP_MS = 280;

function dist(a, b) {
  const dx = a.x - b.x, dy = a.y - b.y;
  return Math.hypot(dx, dy);
}

export default function usePinchZoom({ enabled = true } = {}) {
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const stateRef = useRef({
    pointers: new Map(),
    pinchStart: null,
    panStart: null,
    lastTap: 0,
  });

  const reset = useCallback(() => {
    setScale(1);
    setPan({ x: 0, y: 0 });
  }, []);

  const onPointerDown = useCallback((e) => {
    if (!enabled) return;
    const s = stateRef.current;
    s.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (s.pointers.size === 2) {
      const [a, b] = [...s.pointers.values()];
      s.pinchStart = { d: dist(a, b), scale };
    } else if (s.pointers.size === 1 && scale > 1) {
      s.panStart = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
    }
  }, [enabled, scale, pan]);

  const onPointerMove = useCallback((e) => {
    if (!enabled) return;
    const s = stateRef.current;
    if (!s.pointers.has(e.pointerId)) return;
    s.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (s.pointers.size === 2 && s.pinchStart) {
      const [a, b] = [...s.pointers.values()];
      const d = dist(a, b);
      const next = Math.max(MIN_SCALE, Math.min(MAX_SCALE, s.pinchStart.scale * (d / s.pinchStart.d)));
      setScale(next);
      // Pinch tends to drift pan around — snap back to 0,0 when fully zoomed out.
      if (next === MIN_SCALE) setPan({ x: 0, y: 0 });
    } else if (s.pointers.size === 1 && s.panStart && scale > 1) {
      setPan({
        x: s.panStart.panX + (e.clientX - s.panStart.x),
        y: s.panStart.panY + (e.clientY - s.panStart.y),
      });
    }
  }, [enabled, scale]);

  const onPointerUp = useCallback((e) => {
    if (!enabled) return;
    const s = stateRef.current;
    const wasSingle = s.pointers.size === 1;
    s.pointers.delete(e.pointerId);
    if (s.pointers.size < 2) s.pinchStart = null;
    if (s.pointers.size === 0) s.panStart = null;
    // Double-tap to reset.
    if (wasSingle && !s.pinchStart) {
      const now = Date.now();
      if (now - s.lastTap < DOUBLE_TAP_MS) {
        reset();
        s.lastTap = 0;
      } else {
        s.lastTap = now;
      }
    }
  }, [enabled, reset]);

  const onWheel = useCallback((e) => {
    if (!enabled) return;
    if (Math.abs(e.deltaY) < 1) return;
    e.preventDefault?.();
    setScale((s) => {
      const next = Math.max(MIN_SCALE, Math.min(MAX_SCALE, s * (e.deltaY < 0 ? 1.12 : 1 / 1.12)));
      if (next === MIN_SCALE) setPan({ x: 0, y: 0 });
      return next;
    });
  }, [enabled]);

  return {
    scale,
    pan,
    handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp, onWheel },
    reset,
    isZoomed: scale > 1.01,
  };
}
